import { BookCopy, CopyPlus, GitCompareArrows, LibraryBig, PackageCheck, RefreshCw, UploadCloud } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { RichTextEditor } from '../components/RichTextEditor';
import { Alert, Badge, Button, Card, DateTime, Empty, Field, Form, Loading, Metric, Modal, PageHeader ,Select } from '../components/UI';
import { api, errorText } from '../lib/api';

type LibraryItem = {
  id: number;
  uuid: string;
  title: string;
  item_type: string;
  description?: string | null;
  payload?: Record<string, unknown>;
  tags?: string[];
  visibility: string;
  status: string;
  version: string;
  published_at?: string | null;
  versions?: Array<{ id: number; version: string; change_summary?: string | null; created_at: string }>;
};
type LibraryDashboard = {
  metrics: Record<string, number>;
  items: LibraryItem[];
  versions: Array<Record<string, unknown>>;
  clones: Array<Record<string, unknown>>;
  bulk_batches: Array<Record<string, unknown>>;
};
type DiffResult = { from: string; to: string; diff: { added: string[]; removed: string[]; changed: string[] } };
type ContentProgram = { uuid: string; title: string };
type ContentCurriculum = { id: number; version: string; label?: string; status: string; phases?: Array<{ id: number; title: string; modules?: Array<{ id: number; title: string }> }> };

const defaultPayload = JSON.stringify({
  lesson_title: 'Reusable Pintop lesson',
  type: 'html',
  content_html: '<h2>Learning objectives</h2><p>Teach the idea, demonstrate it, then ask the learner to mark class done before moving forward.</p>',
  completion_rules: { minimum_percent: 90, requires_done_mark: true },
}, null, 2);

export function ContentMarketplacePage() {
  const [dashboard, setDashboard] = useState<LibraryDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [bulkIds, setBulkIds] = useState<number[]>([]);
  const [diff, setDiff] = useState<DiffResult | null>(null);
  const [programs, setPrograms] = useState<ContentProgram[]>([]);
  const [programUuid, setProgramUuid] = useState('');
  const [curricula, setCurricula] = useState<ContentCurriculum[]>([]);
  const [template, setTemplate] = useState({
    title: '',
    item_type: 'template',
    visibility: 'global',
    status: 'draft',
    version: '1.0',
    tags: 'lesson, pintop-brand',
    description: '',
    lesson_title: 'Reusable Pintop lesson',
    content_html: '<h2>Learning objectives</h2><p>Teach the idea, demonstrate it, then ask the learner to mark class done before moving forward.</p>',
    completion_percent: 90,
    requires_done_mark: true,
    payload: defaultPayload,
  });
  const [cloneForm, setCloneForm] = useState({ item_id: '', target_type: 'module', target_id: '', notes: '' });
  const [diffForm, setDiffForm] = useState({ item_id: '', from: '1.0', to: '1.0' });
  const [rowClone, setRowClone] = useState<{ item: LibraryItem; target_type: string; target_id: string; source_version: string; notes: string } | null>(null);
  const [rowDiff, setRowDiff] = useState<{ item: LibraryItem; from: string; to: string } | null>(null);
  const [rowDiffResult, setRowDiffResult] = useState<DiffResult | null>(null);

  const publishedItems = useMemo(() => (dashboard?.items || []).filter(item => item.status === 'published'), [dashboard]);
  const destinationOptions = useMemo(() => {
    if (cloneForm.target_type === 'curriculum_version') return curricula.map(curriculum => ({ id: curriculum.id, label: `v${curriculum.version} - ${curriculum.label || curriculum.status}` }));
    if (cloneForm.target_type === 'phase') return curricula.flatMap(curriculum => (curriculum.phases || []).map(phase => ({ id: phase.id, label: `v${curriculum.version} - ${phase.title}` })));
    if (cloneForm.target_type === 'module') return curricula.flatMap(curriculum => (curriculum.phases || []).flatMap(phase => (phase.modules || []).map(module => ({ id: module.id, label: `v${curriculum.version} - ${phase.title} - ${module.title}` }))));
    return [];
  }, [cloneForm.target_type, curricula]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [response, programResponse] = await Promise.all([api.get<LibraryDashboard>('/admin/content-library'), api.get<unknown>('/admin/programs/options').catch(() => ({ data: [] }))]);
      setDashboard(response.data);
      const programRows = Array.isArray(programResponse.data) ? programResponse.data as ContentProgram[] : ((programResponse.data as { data?: ContentProgram[] })?.data || []);
      setPrograms(programRows);
      if (!programUuid && programRows[0]) setProgramUuid(programRows[0].uuid);
      const first = response.data.items[0];
      if (first) {
        setCloneForm(current => ({ ...current, item_id: current.item_id || String(first.id) }));
        setDiffForm(current => ({ ...current, item_id: current.item_id || String(first.id) }));
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!programUuid) return;
    api.get<ContentCurriculum[]>(`/admin/programs/${programUuid}/curricula`)
      .then(response => setCurricula(response.data))
      .catch(() => setCurricula([]));
  }, [programUuid]);

  const createTemplate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('template');
    setError('');
    try {
      await api.post('/admin/content-library/templates', {
        ...template,
        payload: { lesson_title: template.lesson_title, type: template.item_type, content_html: template.content_html, completion_rules: { minimum_percent: Number(template.completion_percent), requires_done_mark: template.requires_done_mark } },
        tags: template.tags.split(',').map(tag => tag.trim()).filter(Boolean),
      });
      setTemplate(current => ({ ...current, title: '', description: '', lesson_title: 'Reusable Pintop lesson', content_html: '<h2>Learning objectives</h2><p>Teach the idea, demonstrate it, then ask the learner to mark class done before moving forward.</p>', completion_percent: 90, requires_done_mark: true, payload: defaultPayload }));
      setMessage('Reusable content template saved to the shared module library.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const cloneItem = async (event: FormEvent) => {
    event.preventDefault();
    if (!cloneForm.item_id) return;
    setBusy('clone');
    setError('');
    try {
      await api.post(`/admin/content-library/${cloneForm.item_id}/clone`, {
        target_type: cloneForm.target_type,
        target_id: cloneForm.target_id ? Number(cloneForm.target_id) : null,
        notes: cloneForm.notes || null,
      });
      setMessage('Shared content cloned and saved for review.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const compareVersions = async (event: FormEvent) => {
    event.preventDefault();
    if (!diffForm.item_id) return;
    setBusy('diff');
    setError('');
    try {
      const response = await api.get<DiffResult>(`/admin/content-library/${diffForm.item_id}/diff?from=${encodeURIComponent(diffForm.from)}&to=${encodeURIComponent(diffForm.to)}`);
      setDiff(response.data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const publishItem = async (item: LibraryItem) => {
    setBusy(`publish-${item.id}`);
    setError('');
    try {
      await api.post(`/admin/content-library/${item.id}/publish`);
      setMessage(`Published "${item.title}".`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const openCloneItem = (item: LibraryItem) => {
    setRowClone({ item, target_type: 'library_only', target_id: '', source_version: item.version || '', notes: '' });
  };

  const submitCloneItem = async (event: FormEvent) => {
    event.preventDefault();
    if (!rowClone) return;
    setBusy('row-clone');
    setError('');
    try {
      await api.post(`/admin/content-library/${rowClone.item.id}/clone`, {
        target_type: rowClone.target_type,
        target_id: rowClone.target_id ? Number(rowClone.target_id) : null,
        source_version: rowClone.source_version || null,
        notes: rowClone.notes || null,
      });
      setMessage('Shared content cloned and saved for review.');
      setRowClone(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const openDiffItem = (item: LibraryItem) => {
    setRowDiffResult(null);
    setRowDiff({ item, from: item.version || '1.0', to: item.version || '1.0' });
  };

  const submitDiffItem = async (event: FormEvent) => {
    event.preventDefault();
    if (!rowDiff) return;
    setBusy('row-diff');
    setError('');
    try {
      const response = await api.get<DiffResult>(`/admin/content-library/${rowDiff.item.id}/diff?from=${encodeURIComponent(rowDiff.from)}&to=${encodeURIComponent(rowDiff.to)}`);
      setRowDiffResult(response.data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  const bulkPublish = async () => {
    if (!bulkIds.length) return;
    setBusy('bulk');
    setError('');
    try {
      await api.post('/admin/content-library/bulk-publish', {
        title: 'Bulk publish approved reusable content',
        item_ids: bulkIds,
      });
      setBulkIds([]);
      setMessage('Bulk publish completed for selected content library items.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  };

  return <>
    <PageHeader
      eyebrow="Content marketplace"
      title="Reusable content library"
      description="Create reusable content templates, maintain a shared module library, inspect cloning/version diff UI, and bulk publish approved Pintop learning assets."
      actions={<Button kind="secondary" onClick={() => void load()}><RefreshCw /> Refresh library</Button>}
    />
    {message && <Alert type="success" onClose={() => setMessage('')}>{message}</Alert>}
    {error && <Alert onClose={() => setError('')}>{error}</Alert>}
    {loading || !dashboard ? <Loading label="Loading shared content library..." /> : <>
      <div className="metric-grid">
        <Metric label="Reusable templates" value={dashboard.metrics.templates || 0} hint="HTML, resource and assessment shells" />
        <Metric label="Shared modules" value={dashboard.metrics.shared_modules || 0} hint="Clone-ready module bundles" />
        <Metric label="Published assets" value={dashboard.metrics.published || 0} hint="Available to curriculum builders" />
        <Metric label="Bulk batches" value={dashboard.metrics.bulk_batches || 0} hint="Publish runs" />
      </div>

      <div className="dashboard-grid">
        <Card title="Create reusable content template">
          <Form onSubmit={createTemplate} busy={busy === 'template'} submitLabel="Save template">
            <div className="form-grid">
              <Field label="Template title"><input value={template.title} onChange={e => setTemplate({ ...template, title: e.target.value })} required /></Field>
              <Field label="Library type"><Select value={template.item_type} onChange={e => setTemplate({ ...template, item_type: e.target.value })}><option value="template">Reusable template</option><option value="module_bundle">Shared module bundle</option><option value="lesson">Lesson</option><option value="resource">Resource</option><option value="assessment_pack">Assessment pack</option></Select></Field>
              <Field label="Visibility"><Select value={template.visibility} onChange={e => setTemplate({ ...template, visibility: e.target.value })}><option>private</option><option>team</option><option>organization</option><option>global</option></Select></Field>
              <Field label="Status"><Select value={template.status} onChange={e => setTemplate({ ...template, status: e.target.value })}><option>draft</option><option>review</option><option>published</option></Select></Field>
              <Field label="Version"><input value={template.version} onChange={e => setTemplate({ ...template, version: e.target.value })} /></Field>
              <Field label="Tags"><input value={template.tags} onChange={e => setTemplate({ ...template, tags: e.target.value })} /></Field>
              <Field label="Description" wide><RichTextEditor height={220} value={template.description} onChange={value => setTemplate({ ...template, description: value })} placeholder="Reusable content template description" /></Field>
              <Field label="Lesson title"><input value={template.lesson_title} onChange={e => setTemplate({ ...template, lesson_title: e.target.value })} /></Field><Field label="Completion threshold"><input type="number" min="0" max="100" value={template.completion_percent} onChange={e => setTemplate({ ...template, completion_percent: Number(e.target.value) })} /></Field><Field label="Completion rule" wide><label className="check"><input type="checkbox" checked={template.requires_done_mark} onChange={e => setTemplate({ ...template, requires_done_mark: e.target.checked })} /> Learner must mark this class done before moving forward</label></Field><Field label="Lesson content" wide><RichTextEditor height={380} value={template.content_html} onChange={value => setTemplate({ ...template, content_html: value })} placeholder="Reusable lesson content" /></Field>
            </div>
          </Form>
        </Card>

        <Card title="Clone shared module or lesson">
          <Form onSubmit={cloneItem} busy={busy === 'clone'} submitLabel="Clone content">
            <div className="form-grid">
              <Field label="Library item"><Select value={cloneForm.item_id} onChange={e => setCloneForm({ ...cloneForm, item_id: e.target.value })}>{dashboard.items.map(item => <option key={item.id} value={item.id}>{item.title} ({item.version})</option>)}</Select></Field>
              <Field label="Programme"><Select value={programUuid} onChange={e => { setProgramUuid(e.target.value); setCloneForm({ ...cloneForm, target_id: '' }); }}><option value="">Select programme</option>{programs.map(program => <option key={program.uuid} value={program.uuid}>{program.title}</option>)}</Select></Field>
              <Field label="Target type"><Select value={cloneForm.target_type} onChange={e => setCloneForm({ ...cloneForm, target_type: e.target.value })}><option value="module">Existing module</option><option value="phase">Existing phase</option><option value="curriculum_version">Curriculum version</option><option value="library_only">Keep in library only</option></Select></Field>
              {cloneForm.target_type !== 'library_only' && <Field label="Destination"><Select value={cloneForm.target_id} onChange={e => setCloneForm({ ...cloneForm, target_id: e.target.value })}><option value="">Select destination</option>{destinationOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}</Select></Field>}
              <Field label="Clone notes" wide><RichTextEditor height={220} value={cloneForm.notes} onChange={value => setCloneForm({ ...cloneForm, notes: value })} placeholder="Add clone instructions, QA notes or version guidance" /></Field>
            </div>
          </Form>
          <div className="compact-list">
            <div><CopyPlus /><strong>Clone content</strong><span>Creates lessons inside a target module or saves a governed clone for review.</span></div>
            <div><GitCompareArrows /><strong>Version comparison</strong><span>Every clone shows what changed so QA can approve it confidently.</span></div>
          </div>
        </Card>
      </div>

      <div className="dashboard-grid">
        <Card title="Shared module library">
          {dashboard.items.length ? <div className="table-wrap"><table><thead><tr><th></th><th>Asset</th><th>Type</th><th>Version</th><th>Visibility</th><th>Status</th><th>Published</th><th>Actions</th></tr></thead><tbody>{dashboard.items.map(item => <tr key={item.id}><td><input type="checkbox" checked={bulkIds.includes(item.id)} onChange={e => setBulkIds(ids => e.target.checked ? [...ids, item.id] : ids.filter(id => id !== item.id))} /></td><td><strong>{item.title}</strong><small>{item.description || 'Reusable Pintop content asset'}</small><div className="badge-row">{(item.tags || []).map(tag => <Badge key={tag}>{tag}</Badge>)}</div></td><td><Badge>{item.item_type.replaceAll('_', ' ')}</Badge></td><td>{item.version}</td><td>{item.visibility}</td><td><Badge>{item.status}</Badge></td><td><DateTime value={item.published_at} /></td><td><div className="row-actions"><Button kind="secondary" busy={busy === `publish-${item.id}`} onClick={() => void publishItem(item)}><UploadCloud /> Publish</Button><Button kind="ghost" onClick={() => openCloneItem(item)}><CopyPlus /> Clone</Button><Button kind="ghost" onClick={() => openDiffItem(item)}><GitCompareArrows /> Diff</Button></div></td></tr>)}</tbody></table></div> : <Empty title="No reusable content yet" text="Create templates or shared modules to build the marketplace library." />}
          <div className="form-actions">
            <Button kind="secondary" disabled={!publishedItems.length} onClick={() => setBulkIds(publishedItems.map(item => item.id))}><PackageCheck /> Select published</Button>
            <Button busy={busy === 'bulk'} disabled={!bulkIds.length} onClick={() => void bulkPublish()}><UploadCloud /> Bulk publish selected</Button>
          </div>
        </Card>

        <Card title="Version diff UI">
          <Form onSubmit={compareVersions} busy={busy === 'diff'} submitLabel="Compare versions">
            <div className="form-grid">
              <Field label="Library item"><Select value={diffForm.item_id} onChange={e => setDiffForm({ ...diffForm, item_id: e.target.value })}>{dashboard.items.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}</Select></Field>
              <Field label="From"><input value={diffForm.from} onChange={e => setDiffForm({ ...diffForm, from: e.target.value })} /></Field>
              <Field label="To"><input value={diffForm.to} onChange={e => setDiffForm({ ...diffForm, to: e.target.value })} /></Field>
            </div>
          </Form>
          {diff ? <div className="diff-panel">
            <div><Badge>from {diff.from}</Badge><Badge>to {diff.to}</Badge></div>
            <div className="compact-list">
              <div><LibraryBig /><strong>Added</strong><span>{diff.diff.added.join(', ') || 'No added content'}</span></div>
              <div><BookCopy /><strong>Changed</strong><span>{diff.diff.changed.join(', ') || 'No changed content'}</span></div>
              <div><GitCompareArrows /><strong>Removed</strong><span>{diff.diff.removed.join(', ') || 'No removed content'}</span></div>
            </div>
          </div> : <Empty title="No diff loaded" text="Choose two versions to review content changes before publishing or cloning." />}
        </Card>
      </div>
    </>}
    {rowClone && <Modal open title={`Clone ${rowClone.item.title}`} onClose={() => setRowClone(null)}>
      <Form onSubmit={submitCloneItem} busy={busy === 'row-clone'} submitLabel="Clone content" secondary={<Button kind="secondary" onClick={() => setRowClone(null)}>Cancel</Button>}>
        <div className="form-grid">
          <Field label="Target type"><Select value={rowClone.target_type} onChange={e => setRowClone({ ...rowClone, target_type: e.target.value })}><option value="library_only">Keep in library only</option><option value="module">Existing module</option><option value="phase">Existing phase</option><option value="curriculum_version">Curriculum version</option></Select></Field>
          {rowClone.target_type !== 'library_only' && <Field label="Target id" hint="Numeric id of the destination module, phase or curriculum version."><input type="number" min="1" value={rowClone.target_id} onChange={e => setRowClone({ ...rowClone, target_id: e.target.value })} placeholder="Destination id" /></Field>}
          <Field label="Source version"><input value={rowClone.source_version} onChange={e => setRowClone({ ...rowClone, source_version: e.target.value })} placeholder={rowClone.item.version} /></Field>
          <Field label="Notes" wide><textarea rows={3} value={rowClone.notes} onChange={e => setRowClone({ ...rowClone, notes: e.target.value })} placeholder="Clone instructions, QA notes or version guidance" /></Field>
        </div>
      </Form>
    </Modal>}
    {rowDiff && <Modal open title={`Compare versions · ${rowDiff.item.title}`} onClose={() => setRowDiff(null)}>
      <Form onSubmit={submitDiffItem} busy={busy === 'row-diff'} submitLabel="Compare versions" secondary={<Button kind="secondary" onClick={() => setRowDiff(null)}>Close</Button>}>
        <div className="form-grid">
          <Field label="From version"><input value={rowDiff.from} onChange={e => setRowDiff({ ...rowDiff, from: e.target.value })} required /></Field>
          <Field label="To version"><input value={rowDiff.to} onChange={e => setRowDiff({ ...rowDiff, to: e.target.value })} required /></Field>
        </div>
      </Form>
      {rowDiffResult ? <div className="diff-panel">
        <div><Badge>from {rowDiffResult.from}</Badge><Badge>to {rowDiffResult.to}</Badge></div>
        <div className="compact-list">
          <div><LibraryBig /><strong>Added</strong><span>{rowDiffResult.diff.added.join(', ') || 'No added content'}</span></div>
          <div><BookCopy /><strong>Changed</strong><span>{rowDiffResult.diff.changed.join(', ') || 'No changed content'}</span></div>
          <div><GitCompareArrows /><strong>Removed</strong><span>{rowDiffResult.diff.removed.join(', ') || 'No removed content'}</span></div>
        </div>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: '.82rem' }}>{JSON.stringify(rowDiffResult, null, 2)}</pre>
      </div> : <Empty title="No diff loaded" text="Choose two versions to review content changes before publishing or cloning." />}
    </Modal>}
  </>;
}
